/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, LoginCredentials, SignupData, AuthState } from '@/types';
import apiClient, { UserProfile } from '@/services/api';

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  signup: (data: SignupData) => Promise<void>;
  logout: () => void;
  updateUser: (user: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Map API UserProfile to App User type
 */
const mapProfileToUser = (profile: UserProfile): User => {
  return {
    id: profile.userId,
    email: profile.email,
    role: profile.role as User['role'], // Cast to UserRole type
    accountType: (profile.accountType as User['accountType']) || 'individual',
    firstName: profile.firstName || '',
    lastName: profile.lastName || '',
    createdAt: new Date().toISOString(),
    company: profile.orgId,
    orgId: profile.orgId,
    tenantId: profile.tenantId,
    onboardingCompleted: profile.onboardingCompleted,
  };
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
  });

  // Check for existing session on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Check if we have an access token
        if (apiClient.isAuthenticated()) {
          // Try to fetch user profile from API
          const response = await apiClient.getProfile();
          
          if (response.success && response.data) {
            const user = mapProfileToUser(response.data);
            setAuthState({
              user,
              isAuthenticated: true,
              isLoading: false,
            });
          } else {
            // Token exists but invalid/expired
            apiClient.logout();
            setAuthState({
              user: null,
              isAuthenticated: false,
              isLoading: false,
            });
          }
        } else {
          // No token, user not authenticated
          setAuthState({
            user: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        apiClient.logout();
        setAuthState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
        });
      }
    };

    checkAuth();
  }, []);

  const login = async (credentials: LoginCredentials) => {
    try {
      // Call real API endpoint
      const response = await apiClient.login({
        email: credentials.email,
        password: credentials.password,
      });

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Login failed');
      }

      // Map user profile to User type
      const user = mapProfileToUser(response.data.user);

      setAuthState({
        user,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (error) {
      console.error('Login failed:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Invalid credentials');
    }
  };

  const signup = async (data: SignupData) => {
    try {
      // Call real API endpoint
      const response = await apiClient.signup({
        email: data.email,
        password: data.password,
        role: data.role,
        firstName: data.firstName,
        lastName: data.lastName,
        orgName: data.company,
        orgSubdomain: data.company?.toLowerCase().replace(/[^a-z0-9]/g, '-'),
      });

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Signup failed');
      }

      // After successful signup, user needs to verify email
      // We don't auto-login, redirect to verification page instead
      // The calling component should handle navigation
    } catch (error) {
      console.error('Signup failed:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Signup failed');
    }
  };

  const logout = () => {
    apiClient.logout();
    setAuthState({
      user: null,
      isAuthenticated: false,
      isLoading: false,
    });
  };

  const updateUser = async (updates: Partial<User>) => {
    if (authState.user) {
      // Optimistically update local state
      const updatedUser = { ...authState.user, ...updates };
      setAuthState({
        ...authState,
        user: updatedUser,
      });

      // In a real app, we'd call an API endpoint to update the user profile
      // For now, just update local state
      // TODO: Implement PUT /auth/profile endpoint
    }
  };

  return (
    <AuthContext.Provider
      value={{
        ...authState,
        login,
        signup,
        logout,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
